import * as React from 'react';
import * as SelectPrimitive from './Select';
import { FloatingLabel } from '../FloatingLabel/';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { SelectProps } from './types';

export function Select({
  labelProps,
  children,
  placeholder,
  showClearButton,
  clearButtonProps,
  chevronButtonProps,
  value,
  defaultValue,
  onValueChange,
  className,
  triggerProps,
  error = false,
  ...props
}: SelectProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue ?? '');
  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;
  const { className: labelClassName, ...restLabelProps } = labelProps ?? {};
  const { className: triggerClassName, ...restTriggerProps } =
    triggerProps ?? {};

  const handleChange = (val: string) => {
    if (!isControlled) setInternalValue(val);
    onValueChange?.(val);
  };

  const handleClear = (e: React.MouseEvent) => {
    // otherwise trigger button is stealing the click event
    e.preventDefault();
    e.stopPropagation();

    handleChange('');
  };

  return (
    <SelectPrimitive.Select
      value={currentValue}
      onValueChange={handleChange}
      {...props}
    >
      <SelectPrimitive.SelectTrigger
        className={cn(
          'eye:group eye:flex eye:h-[44px] eye:outline-none eye:focus-visible:outline-none',
          triggerClassName,
        )}
        {...restTriggerProps}
      >
        <FloatingLabel
          renderAsSpan={true}
          className={cn(
            'eye:m-0 eye:min-w-200 eye:outline-transparent eye:transition-[outline-color]',
            'eye:group-focus-visible:outline-2 eye:group-focus-visible:outline-offset-3' +
              ' eye:group-focus-visible:outline-black',
            error && 'eye:border-red-100 eye:hover:border-red-100',
            labelClassName,
            className,
          )}
          required={props.required}
          disabled={props.disabled}
          {...restLabelProps}
          rightSlot={
            <>
              {showClearButton && currentValue && !props.disabled && (
                <span
                  {...clearButtonProps}
                  className='eye:flex eye:cursor-pointer eye:items-center'
                  onPointerDown={(e) => {
                    // otherwise trigger button is stealing the click event
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onMouseDown={(e) => {
                    // otherwise trigger button is stealing the click event
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={handleClear}
                >
                  <X />
                </span>
              )}
              <span
                className={cn(!props.disabled && 'eye:cursor-pointer')}
                {...chevronButtonProps}
              >
                <ChevronDown />
              </span>
            </>
          }
        >
          <span
            className={cn(
              currentValue ? 'eye:text-black' : 'eye:text-grey-placeholder',
            )}
          >
            <SelectPrimitive.SelectValue placeholder={placeholder} />
          </span>
        </FloatingLabel>
      </SelectPrimitive.SelectTrigger>

      <SelectPrimitive.SelectContent
        position='popper'
        side='bottom'
        align={'start'}
        sideOffset={labelProps?.float === 'top' ? 5 : -1}
      >
        {children}
      </SelectPrimitive.SelectContent>
    </SelectPrimitive.Select>
  );
}
